import Form from "rc-field-form";
import { useCallback, useEffect } from "react";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import Button from "../../../../components/Button";
import Field from "../../../../components/Field";
import NumberInput from "../../../../components/Inputs/NumberInput";
import ResourceSelect from "../../../../components/Inputs/ResourceSelect";
import Text from "../../../../components/Inputs/Text";
import { generateErrorsConfigForForm } from "../../../../utils/helpers/errors";
import {
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
} from "../../../../utils/helpers/settings";
import { useCompanyDateFormat } from "../../../../utils/hooks/company";
import { EffectiveDateFields, EffectivePreview, SubmitLabel, useEffectiveSubmit } from "./effective";

function AssignForm({ mode, close, values, error, loading, create, effective, schedule }) {
	const { t } = useTranslation();
	const [form] = Form.useForm();
	const dateFormat = useCompanyDateFormat() || "MM/DD/YYYY";
	// a new plan can start on another day; a clone keeps the plain create
	const withDate = mode === "create";
	const { preview, setPreview, previewing, submit } = useEffectiveSubmit({ mode, values, effective, schedule });

	const onFinish = useCallback(async ({ effectiveDate, ...data }) => {
		data.plan = data.plan.id;
		data.amount = Number(data.amount) || 0;
		if (withDate && await submit({ ...data, effectiveDate })) {
			return;
		}
		create(data, close);
	}, [create, close, withDate, submit]);

	useEffect(() => {
		form.resetFields();
		form.setFieldsValue({
			plan: values?.plan,
			amount: 0,
			comment: "",
			effectiveDate: withDate ? moment().toDate() : undefined,
		});
		setPreview(null);
	}, [form, values, mode, withDate, setPreview]);

	useEffect(() => {
		const fieldErrors = generateErrorsConfigForForm(["data"], error);
		form.setFields(fieldErrors);
	}, [form, error]);

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={onFinish}
			// a confirmed preview must be the one on screen: any change asks for a new one
			onValuesChange={() => setPreview(null)}
			scrollToFirstError
		>
			<Field
				name="plan"
				label={t("plan")}
				rules={[
					{
						required: true,
						message: t("required-plan"),
					},
				]}
			>
				<ResourceSelect
					labelPropName="description"
					resourcePath="/timeoff?status=active"
					renderLabel={renderSettingsResourceLabel}
					placeholder={t("plan")}
					hasSearch
					getSearchFilters={getSettingsResourceSearchFilters}
				/>
			</Field>

			<Field
				name="amount"
				label={t("amount")}
				rules={[
					{
						required: true,
						message: t("amount-is-required"),
					},
				]}
			>
				<NumberInput placeholder={t("amount")} />
			</Field>

			{values && (
				<Field name="comment" label={t("comment")}>
					<Text placeholder={t("comment")} />
				</Field>
			)}

			{withDate && <EffectiveDateFields mode={mode} t={t} dateFormat={dateFormat} />}
			{preview && <EffectivePreview preview={preview} t={t} dateFormat={dateFormat} />}

			<div className="d-flex justify-content-end">
				<Button
					className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
					onClick={close}
					disabled={loading}
				>
					{t("cancel")}
				</Button>

				<Button
					color="primary"
					type="submit"
					loading={loading || previewing}
					disabled={!!preview?.blocked?.length}
					className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
				>
					<SubmitLabel t={t} preview={preview} withDate={withDate} />
				</Button>
			</div>
		</Form>
	);
}

export default AssignForm;
